import styled from '@emotion/styled';
import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import {
  applyMatchLayout,
  authoredBundlesBbox,
  collectSurvivingBundles,
  DEFAULT_MATCH_LAYOUT,
  MatchLayoutAlignment,
  MatchLayoutAxis,
  MatchLayoutSettings,
  MatchLayoutSnapshot,
  revertMatchLayout,
} from 'modules/manager/domain/match-layout';
import { isMultiMatchTemplateType } from 'modules/manager/domain/placeholder/placeholder-key-registry';
import { selectActiveTemplate, setMatchLayoutLocal, updateMatchLayout } from 'modules/manager/store';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import CommitOptionalNumberField from '../shared/commit-optional-number-field';
import HelpBubble from '../shared/help-bubble';
import InspectorRow from '../shared/inspector-row';
import InspectorSection from '../shared/inspector-section';
import SelectField from '../shared/select-field';

interface CommonMatchLayoutSectionProps {
  editor: FabricEditor;
}

const AXIS_OPTIONS = [
  { value: 'auto', label: 'Авто' },
  { value: 'vertical', label: 'Вертикально' },
  { value: 'horizontal', label: 'Горизонтально' },
];

const ALIGNMENT_OPTIONS = [
  { value: 'center', label: 'Центр' },
  { value: 'start', label: 'Начало' },
  { value: 'end', label: 'Конец' },
  { value: 'distribute', label: 'Распределить' },
];

const CommonMatchLayoutSection: React.FC<CommonMatchLayoutSectionProps> = ({ editor }) => {
  const dispatch = useAppDispatch();
  const template = useAppSelector(selectActiveTemplate);
  const settings = template?.matchLayout ?? DEFAULT_MATCH_LAYOUT;
  const isMultiMatch = isMultiMatchTemplateType(editor.placeholders?.getTemplateType());

  const authoredCount = useMemo(
    () => countAuthoredMatches(template?.objects ?? []),
    [template?.objects],
  );

  const [previewCount, setPreviewCount] = useState<number | undefined>(undefined);
  const previewStateRef = useRef<{ snapshot: MatchLayoutSnapshot; hidden: fabric.Object[] } | null>(
    null,
  );

  const revertPreview = useCallback(() => {
    const state = previewStateRef.current;
    if (!state) return;
    revertMatchLayout(state.snapshot);
    for (const obj of state.hidden) obj.set({ visible: true });
    previewStateRef.current = null;
    editor.canvas.requestRenderAll();
  }, [editor]);

  const applyPreview = useCallback(
    (count: number) => {
      revertPreview();
      if (!template) return;
      const authoredRegion = authoredBundlesBbox(template.objects ?? []);
      if (!authoredRegion) return;
      editor.canvas.discardActiveObject();
      const hidden: fabric.Object[] = [];
      for (const obj of editor.canvas.getObjects()) {
        const n = (obj.data as { matchNumber?: number } | undefined)?.matchNumber;
        if (typeof n === 'number' && n > count) {
          obj.set({ visible: false });
          hidden.push(obj);
        }
      }
      const surviving = collectSurvivingBundles(
        editor.canvas.getObjects().filter((o) => o.visible !== false),
      );
      const snapshot = applyMatchLayout({ surviving, authoredRegion, settings });
      previewStateRef.current = { snapshot, hidden };
      editor.canvas.requestRenderAll();
    },
    [editor, settings, template, revertPreview],
  );

  useEffect(() => {
    if (previewCount === undefined || previewCount >= authoredCount || authoredCount === 0) {
      revertPreview();
      return;
    }
    applyPreview(previewCount);
  }, [previewCount, authoredCount, settings, applyPreview, revertPreview]);

  useEffect(() => {
    const off = editor.on('selection:changed', (e) => {
      if (e.active.length > 0 && previewStateRef.current) {
        setPreviewCount(undefined);
      }
    });
    return off;
  }, [editor]);

  useEffect(() => () => revertPreview(), [revertPreview]);

  const commitSettings = useCallback(
    (next: MatchLayoutSettings) => {
      if (!template) return;
      dispatch(setMatchLayoutLocal(next));
      dispatch(updateMatchLayout({ id: template.id, matchLayout: next }));
    },
    [dispatch, template],
  );

  if (!isMultiMatch || !template) return null;

  return (
    <InspectorSection title="Раскладка матчей">
      <InspectorRow label="Включено">
        <ToggleRow>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => commitSettings({ ...settings, enabled: e.target.checked })}
          />
          <HelpBubble
            content={
              <span>
                Если количество матчей при рендере меньше, чем в шаблоне, блоки оставшихся матчей
                перераспределяются внутри исходной области по заданной оси и выравниванию.
                Блоки определяются автоматически по <code>matchNumber</code>.
              </span>
            }
          />
        </ToggleRow>
      </InspectorRow>
      <InspectorRow label="Ось">
        <SelectField
          value={settings.axis}
          onChange={(v) => commitSettings({ ...settings, axis: v as MatchLayoutAxis })}
          options={AXIS_OPTIONS}
        />
      </InspectorRow>
      <InspectorRow label="Выравнивание">
        <SelectField
          value={settings.alignment}
          onChange={(v) => commitSettings({ ...settings, alignment: v as MatchLayoutAlignment })}
          options={ALIGNMENT_OPTIONS}
        />
      </InspectorRow>
      <Divider />
      <InspectorRow label="Предпросмотр">
        <PreviewRow>
          <CommitOptionalNumberField
            value={previewCount}
            placeholder={authoredCount > 0 ? `1..${authoredCount}` : '—'}
            min={1}
            max={authoredCount > 0 ? authoredCount : undefined}
            onCommit={(v) => setPreviewCount(clampPreview(v, authoredCount))}
          />
          <HelpBubble
            content={
              <span>
                Временно рендерит шаблон так, будто передано только N матчей — удобно для проверки
                выравнивания. Не сохраняется. При выборе объекта предпросмотр сбрасывается.
              </span>
            }
          />
        </PreviewRow>
      </InspectorRow>
    </InspectorSection>
  );
};

export default CommonMatchLayoutSection;

function countAuthoredMatches(objects: readonly unknown[]): number {
  let max = 0;
  for (const raw of objects) {
    const n = (raw as { data?: { matchNumber?: number } } | undefined)?.data?.matchNumber;
    if (typeof n === 'number' && n > max) max = n;
  }
  return max;
}

function clampPreview(value: number | undefined, authoredCount: number): number | undefined {
  if (value === undefined) return undefined;
  if (!Number.isFinite(value) || value < 1) return undefined;
  if (authoredCount <= 0) return undefined;
  if (value >= authoredCount) return undefined;
  return Math.floor(value);
}

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

const PreviewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  min-width: 0;

  & > :first-of-type {
    flex: 1;
    min-width: 0;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 4px 0;
`;
